import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../shell/backofficeSidebar';
import { useAuth } from '../../../../context/AuthContext';
import WorkspaceTechnicalDocumentationPage from './WorkspaceTechnicalDocumentationPage';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
const TECH_DOCS_TOKEN_KEY = 'lewouhmart_tech_docs_token';

function TechnicalDocsGatePage() {
  const { t } = useTranslation();
  const location = useLocation();
  const sidebarItems = getBackofficeSidebarItems(t);
  const { authorizedFetch } = useAuth();

  const [accessKey, setAccessKey] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [token, setToken] = React.useState<string | null>(() => {
    try {
      return window.sessionStorage.getItem(TECH_DOCS_TOKEN_KEY);
    } catch {
      return null;
    }
  });

  const onLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/docs/technical/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessKey }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || 'Access denied');
      }
      const data = await res.json();
      const next = String((data as { token?: string }).token || '');
      if (!next) throw new Error('Access denied');
      setToken(next);
      try {
        window.sessionStorage.setItem(TECH_DOCS_TOKEN_KEY, next);
      } catch {
        // ignore
      }
      setAccessKey('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Access denied');
    } finally {
      setBusy(false);
    }
  };

  const onLogout = () => {
    setToken(null);
    try {
      window.sessionStorage.removeItem(TECH_DOCS_TOKEN_KEY);
    } catch {
      // ignore
    }
  };

  if (token) {
    return (
      <WorkspaceTechnicalDocumentationPage token={token} onLogout={onLogout} />
    );
  }

  return (
    <MainLayout withSidebar sidebarItems={sidebarItems} activePath={location.pathname}>
      <main className="workspace-page">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">
            Technical documentation (restricted)
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            This manual contains engineering details (architecture, security design, and internal contracts). Enter the technical docs access key to continue.
          </p>
          {error ? (
            <p className="mt-3 text-xs text-red-600">{error}</p>
          ) : null}
          <form className="mt-4 max-w-md space-y-3" onSubmit={onLogin}>
            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-700" htmlFor="techDocsKey">
                Access key
              </label>
              <input
                id="techDocsKey"
                type="password"
                autoComplete="off"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-700 focus:ring-2 focus:ring-slate-300/70"
                value={accessKey}
                onChange={(e) => setAccessKey(e.target.value)}
              />
            </div>
            <button
              type="submit"
              disabled={busy || !accessKey.trim()}
              className="inline-flex w-full items-center justify-center rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? 'Verifying…' : 'Unlock technical docs'}
            </button>
          </form>
        </section>
      </main>
    </MainLayout>
  );
}

export default TechnicalDocsGatePage;

